/** Pure geometry for the UnifiedBoard's board/gantt drag splitter, extracted so
 *  the clamp behaviour is unit-tested without a DOM. The board sits on top at a
 *  fixed pixel height; the gantt fills the rest. Dragging the handle changes the
 *  board height, clamped so neither pane collapses below its minimum. */

export interface SplitClampOpts {
  /** Total height available for both panes + the handle (px). */
  containerPx: number;
  /** Handle thickness (px) — reserved out of the usable space. */
  handlePx: number;
  /** Smallest the board pane may shrink to (px). */
  minBoardPx: number;
  /** Smallest the gantt pane may shrink to (px). */
  minGanttPx: number;
}

/** Clamp a proposed board height so both panes keep at least their minimum.
 *  When the container is too small to honour both minimums, the board minimum
 *  wins (the board is the primary surface) but never exceeds the usable space. */
export function clampBoardHeight(proposedPx: number, opts: SplitClampOpts): number {
  const { containerPx, handlePx, minBoardPx, minGanttPx } = opts;
  const usable = Math.max(0, containerPx - handlePx);
  const maxBoard = usable - minGanttPx;
  // Too cramped to satisfy both: give the board what it can, capped at usable.
  if (maxBoard < minBoardPx) return Math.min(minBoardPx, usable);
  if (proposedPx < minBoardPx) return minBoardPx;
  if (proposedPx > maxBoard) return maxBoard;
  return proposedPx;
}

/** The board-heavy default: the board takes the bulk of the height, leaving the
 *  gantt an initial slice (its minimum, or a comfortable default — whichever is
 *  larger), all within the clamp. Matches the mock's board-heavy open. */
export function defaultBoardHeight(opts: SplitClampOpts, ganttOpenPx = 320): number {
  const { containerPx, handlePx } = opts;
  const usable = Math.max(0, containerPx - handlePx);
  return clampBoardHeight(usable - ganttOpenPx, opts);
}
